import React from 'react'

import {
    StyledTerms,
    StyledLinkList,
    StyledLinks,
    StyledTermsFooter,
    StyledFooterLogo,
} from 'component-library/navigation/TermsFooter/TermsFooter.styled'
import { WebLink } from 'common/navigation/web-link'
import { ThemedRender } from 'component-library/__styling/themed-render'
import {
    theWestSectionDefinitions,
    TheWestSection,
} from 'component-library/__styling/settings/sections/thewest'
import { MastHeadLogo } from 'component-library/content/TheWestHeader/logos/MastheadLogo'
import { useTheme } from '@emotion/react'
import { SevenNewsSection } from 'component-library/__styling/settings/sections/sevennews'
import { useIsEuMemberState } from 'common/data/location-detection/location-detection'

interface TermLink {
    name: string
    link: string
}

export interface Props {
    footerCompanyLinks?: TermLink[]
    footerTermsLinks: TermLink[] | { [section in SevenNewsSection]: TermLink[] }
}

function getAdvertiseLink(section: TheWestSection): TermLink {
    const genLink = (title: string) => ({
        name: `Advertise in ${title}`,
        link: 'https://advertising.sevenwestmedia.com.au',
    })

    const sectionTitle = theWestSectionDefinitions[section as TheWestSection].title

    switch (section) {
        case 'the-kimberley-echo':
        case 'default':
            return genLink(sectionTitle) // already has a 'The' in the title
        default:
            return genLink(`the ${sectionTitle}`) // we want other regional sites to be prefixed with 'the'
    }
}

function getPlaceanadLink(section: TheWestSection): TermLink {
    const genLink = (title: string) => ({
        name: `Place an ad in ${title}`,
        link:
            'https://placeanad.thewest.com.au/WebOE/?utm_source=thewest&utm_medium=referral&utm_campaign=placeanad&utm_content=footerlink',
    })

    switch (section) {
        case 'default':
            return genLink(`the Classifieds`)
        default:
            return genLink(`The West`)
    }
}

export const TermsFooter: React.FC<Props> = ({ footerTermsLinks, footerCompanyLinks }) => {
    const theme = useTheme()

    const renderStyledLinkItem = (linkItem: TermLink) => (
        <StyledLinks key={linkItem.name}>
            <WebLink to={linkItem.link}>{linkItem.name}</WebLink>
        </StyledLinks>
    )

    const renderFooterCompanyLinks = () => {
        if (!footerCompanyLinks) {
            return null
        }

        const footerLinks = [...footerCompanyLinks]

        if (theme.kind === 'thewest') {
            // If the west or regional then we do the following
            const insertIndex = footerCompanyLinks.length > 0 ? footerCompanyLinks.length - 1 : 0

            // get the sectioned advertising link
            const advertisingLink = getAdvertiseLink(theme.section as TheWestSection)
            const placeanadLink = getPlaceanadLink(theme.section as TheWestSection)

            // Insert as second last item, mutates footerCompanyLinks
            footerLinks.splice(insertIndex, 0, placeanadLink, advertisingLink)
        }
        return <StyledLinkList>{footerLinks.map(renderStyledLinkItem)}</StyledLinkList>
    }

    const userInGdprZone = useIsEuMemberState()

    return (
        <StyledTermsFooter>
            <React.Fragment>
                {renderFooterCompanyLinks()}
                <StyledTerms>
                    <ThemedRender
                        thewest={() => {
                            const { title } = theWestSectionDefinitions[
                                theme.section as TheWestSection
                            ]
                            return (
                                <p>
                                    &copy;
                                    {` ${title} and Seven West Media (WA). All rights
                                reserved.`}
                                </p>
                            )
                        }}
                    />
                    <StyledLinkList>
                        {Array.isArray(footerTermsLinks)
                            ? footerTermsLinks.map(renderStyledLinkItem)
                            : footerTermsLinks[theme.section as SevenNewsSection].map(
                                  renderStyledLinkItem
                              )}
                        {userInGdprZone && (
                            <StyledLinks>
                                <WebLink to="/cookie-settings">Cookie Settings</WebLink>
                            </StyledLinks>
                        )}
                    </StyledLinkList>
                    <ThemedRender
                        thewest={() => (
                            <StyledFooterLogo>
                                <MastHeadLogo />
                            </StyledFooterLogo>
                        )}
                    />
                </StyledTerms>
            </React.Fragment>
        </StyledTermsFooter>
    )
}
